import { useState } from 'react';

/**
 * Image carousel with Next/Previous controls and a caption.
 * Shared by every project so the look stays consistent — the only thing meant to
 * vary per project is the image size, set via `maxWidth` (or `imageStyle.width`).
 * Relies on the gallery styles defined in projects.css (#gallery, .galleryimages,
 * .gallerycontrols, .navigatenext, .navigateprevious, #description).
 *
 * @param {{ images: { file: string, description?: string }[], maxWidth?: string, imageStyle?: object, alt?: string }} props
 */
export const Gallery = ({ images, imageStyle, maxWidth, alt = 'Project image' }) => {
  const [index, setIndex] = useState(0);

  if (!images || images.length === 0) return null;

  const count = images.length;
  const current = images[index];
  const go = (delta) => setIndex((i) => (i + delta + count) % count);

  // Image and control bar both fill the container, so sizing the container keeps
  // them aligned. maxWidth (or a width carried over in imageStyle) is the per-project knob.
  const width = maxWidth || (imageStyle && imageStyle.width) || '680px';
  const isVideo = current.video || /\.(mp4|webm|ogg|mov)$/i.test(current.file || '');

  const single = count === 1;

  return (
    <div id="gallery" className={single ? 'singleitem' : undefined} style={{ maxWidth: width }}>
      {isVideo ? (
        <video
          className="galleryvideo"
          src={current.file}
          controls
          playsInline
          preload="metadata"
        />
      ) : (
        <img
          className="galleryimages"
          src={current.file}
          alt={current.description || alt}
        />
      )}
      <div className="gallerycontrols">
        {!single && (
          <button className="navigateprevious" onClick={() => go(-1)}>Previous</button>
        )}
        <div id="description">
          <p>{current.description}</p>
        </div>
        {!single && (
          <button className="navigatenext" onClick={() => go(1)}>Next</button>
        )}
      </div>
    </div>
  );
};

export default Gallery;
